// Enhanced Service Content - Human-written, SEO-optimized with internal links
// Designed for LLM/AI visibility and local SEO dominance in Odessa/Midland

export const serviceContent = {
  "waterline-replacement": {
    metaTitle: "Waterline Replacement Odessa & Midland TX | Basin Plumbing",
    metaDescription: "Expert waterline replacement in Odessa & Midland TX. Trenchless options, 24/7 emergency service. Licensed plumbers. Call (432) 552-8385 for free estimate.",
    h1: "Waterline Replacement in Odessa & Midland",
    heroSubtitle: "Your main water line is the lifeline of your home's plumbing system. When it fails, you need experts who can diagnose fast and fix it right.",
    
    // Stats for visual impact
    stats: [
      { value: "500+", label: "Water Lines Replaced" },
      { value: "Same Day", label: "Emergency Response" },
      { value: "50+ Year", label: "Pipe Lifespan" },
      { value: "24/7", label: "Available" }
    ],

    // What we optimize section (like the reference site)
    quickServices: [
      "Main Water Line Replacement",
      "Leak Detection & Repair", 
      "Pipe Relining",
      "Trenchless Replacement",
      "Emergency Repairs",
      "Pressure Testing"
    ],

    sections: [
      {
        type: "intro",
        h2: "Why Odessa & Midland Homeowners Trust Us With Their Water Lines",
        content: `A local family in West Odessa called us last month in a panic—their water bill had tripled, but they couldn't find a leak anywhere. Turns out, their 40-year-old galvanized water line had corroded underground, leaking 50 gallons a day into their yard.

We replaced their entire water line with modern PEX piping in one day. Their water pressure doubled, their bill dropped by $200/month, and they finally have peace of mind.

That's what proper waterline replacement does. It's not just fixing a problem—it's upgrading your home's entire water delivery system.`
      },
      {
        type: "signs",
        h2: "5 Warning Signs Your Water Line Needs Attention",
        items: [
          {
            title: "Water Pressure Has Dropped",
            content: "If multiple fixtures have weak flow, the problem is likely in your main line—not individual fixtures. We can test your line pressure to confirm.",
            link: { text: "Schedule a pressure test", url: "/contact" }
          },
          {
            title: "Your Water Bill Keeps Climbing",
            content: "A sudden spike without increased usage usually means water is going somewhere it shouldn't. Even small underground leaks waste thousands of gallons monthly.",
            link: { text: "Request leak detection", url: "/contact" }
          },
          {
            title: "Rusty or Discolored Water",
            content: "Brown or yellow water from your taps means your pipes are corroding from the inside. This affects water quality and will only get worse.",
            link: { text: "Get a water quality assessment", url: "/contact" }
          },
          {
            title: "Wet Spots in Your Yard",
            content: "Soggy areas along your water line path—especially when it hasn't rained—are a dead giveaway of an underground leak.",
            link: { text: "Learn about our trenchless options", url: "/services/trenchless-plumbing" }
          },
          {
            title: "Your Home Was Built Before 1980",
            content: "Older homes often have galvanized steel or lead pipes that deteriorate over time. If your water line is original to the house, it's living on borrowed time.",
            link: { text: "Schedule an inspection", url: "/contact" }
          }
        ]
      },
      {
        type: "process",
        h2: "Our Waterline Replacement Process",
        steps: [
          {
            number: "01",
            title: "Free Video Inspection",
            content: "We send a camera down your existing line to see exactly what's happening. No guessing, no unnecessary work."
          },
          {
            number: "02", 
            title: "Upfront Quote",
            content: "You get a written estimate with all costs before we touch anything. The price we quote is the price you pay."
          },
          {
            number: "03",
            title: "Professional Installation",
            content: "Our licensed plumbers install your new line using PEX, copper, or HDPE—whichever is best for your situation and budget."
          },
          {
            number: "04",
            title: "Pressure Testing",
            content: "We test every installation to ensure zero leaks and optimal water pressure throughout your home."
          },
          {
            number: "05",
            title: "Site Cleanup",
            content: "We leave your property as clean as we found it. Often cleaner."
          }
        ]
      },
      {
        type: "comparison",
        h2: "Traditional vs. Trenchless: Which Is Right for You?",
        content: "Not sure if you need traditional excavation or can use our trenchless methods? Here's how we help you decide:",
        options: [
          {
            title: "Traditional Replacement",
            best: "Best when pipes are completely collapsed or need upsizing",
            features: ["Complete access to the line", "Can upgrade pipe diameter", "Lower material cost", "2-3 day completion"],
            link: { text: "Learn more about our process", url: "/contact" }
          },
          {
            title: "Trenchless Replacement", 
            best: "Best when you want to preserve landscaping and driveways",
            features: ["Minimal excavation", "Often same-day completion", "Preserves your yard", "50+ year pipe life"],
            link: { text: "Explore trenchless options", url: "/services/trenchless-plumbing" }
          }
        ]
      },
      {
        type: "pricing",
        h2: "Waterline Replacement Costs in the Permian Basin",
        content: "Straight talk on pricing—because you deserve to know what you're looking at before calling.",
        priceItems: [
          { service: "Spot Repair (per leak)", range: "$500 - $1,500" },
          { service: "Full Line Replacement (Traditional)", range: "$2,000 - $5,000" },
          { service: "Full Line Replacement (Trenchless)", range: "$3,000 - $6,000" },
          { service: "Emergency Service", range: "Starting at $250" }
        ],
        note: "Final cost depends on line length, depth, and soil conditions. We offer financing through GreenSky for larger projects.",
        link: { text: "Get your free estimate", url: "/contact" }
      },
      {
        type: "cta-mid",
        h2: "Not Sure What You Need?",
        content: "Most water line problems start small and get expensive. A quick inspection can save you thousands.",
        buttons: [
          { text: "Schedule Free Inspection", url: "/contact", primary: true },
          { text: "Call (432) 552-8385", url: "tel:4325528385", primary: false }
        ]
      },
      {
        type: "local",
        h2: "Water Line Experts Serving All of West Texas",
        content: `We're based right here in Odessa, and we know the unique challenges Permian Basin homes face. The clay soil here expands and contracts with our crazy temperature swings, putting stress on underground pipes that plumbers from other regions don't understand.

Whether you're in a 1960s ranch in Midland, a new build in Odessa, or a farmhouse out past Andrews, we've worked on homes just like yours.`,
        areas: ["Odessa", "Midland", "Andrews", "Big Spring", "Crane", "Kermit", "Monahans", "Pecos", "Stanton", "Seminole"],
        link: { text: "See all service areas", url: "/plumbers-in-midland" }
      }
    ],
    faqs: [
      {
        question: "How long does waterline replacement take in Odessa?",
        answer: "Most replacements take 1-2 days. Trenchless methods can often be completed in a single day. We'll give you a timeline during your free estimate."
      },
      {
        question: "What type of pipe do you recommend for West Texas?",
        answer: "For our soil and climate, we typically recommend PEX or HDPE. Both handle ground movement well and last 50+ years. We'll explain the pros and cons of each option for your specific situation."
      },
      {
        question: "Do I need a permit for waterline replacement?",
        answer: "Yes, permits are required in Odessa and Midland. We handle all the paperwork and inspections—it's included in our service."
      },
      {
        question: "Will you have to dig up my whole yard?",
        answer: "Not necessarily. Our trenchless methods require only small access points. We'll show you both options and help you choose what's best for your property."
      },
      {
        question: "Can I get financing for waterline replacement?",
        answer: "Yes, we offer financing through GreenSky with affordable monthly payments. Many customers pay less than $100/month for a complete water line replacement."
      },
      {
        question: "What's included in your free estimate?",
        answer: "We'll inspect your current line, explain what we find, give you options with exact pricing, and answer every question. No pressure, no obligation."
      }
    ],
    internalLinks: [
      { text: "trenchless pipe replacement", url: "/services/trenchless-plumbing" },
      { text: "emergency plumbing services", url: "/contact" },
      { text: "drain cleaning", url: "/services/drain-cleaning-midland-odessa-texas" },
      { text: "plumbers in Midland", url: "/plumbers-in-midland" },
      { text: "sewer line services", url: "/services/sewer-line-replacement" }
    ]
  },

  "gas-line-replacement-midland-odessa-texas": {
    metaTitle: "Gas Line Replacement & Repair Odessa Midland TX | Basin Plumbing",
    metaDescription: "Licensed gas line services in Odessa & Midland. Installation, repair, leak detection. 24/7 emergency response. Call (432) 552-8385.",
    h1: "Gas Line Services in Odessa & Midland",
    heroSubtitle: "Gas work isn't DIY territory. Our licensed plumbers handle installation, repair, and emergencies with the expertise your safety demands.",
    
    stats: [
      { value: "300+", label: "Gas Lines Installed" },
      { value: "30 Min", label: "Emergency Response" },
      { value: "100%", label: "Safety Record" },
      { value: "Licensed", label: "& Insured" }
    ],

    quickServices: [
      "Gas Line Installation",
      "Leak Detection & Repair",
      "Appliance Connections",
      "Gas Line Replacement",
      "Pressure Testing",
      "Emergency Service"
    ],

    sections: [
      {
        type: "intro",
        h2: "Gas Line Experts You Can Trust",
        content: `Last summer, a homeowner in Midland smelled gas near their outdoor kitchen—a setup that had been "installed" by a handyman two years earlier. When we inspected it, we found three code violations that could have caused an explosion.

We replaced the entire line with proper materials, added a dedicated shut-off valve, and pressure-tested everything. Now they can grill without worrying about whether their family is safe.

Gas work requires proper licensing, the right materials, and zero shortcuts. That's exactly what we deliver.`
      },
      {
        type: "services-grid",
        h2: "Gas Line Services We Provide",
        items: [
          {
            title: "New Gas Line Installation",
            content: "Adding a gas line for your new range, dryer, fireplace, pool heater, or outdoor grill. We size lines correctly for proper flow.",
            icon: "Flame"
          },
          {
            title: "Gas Leak Detection",
            content: "Using electronic detection equipment to find leaks fast—even in walls and underground.",
            icon: "Search"
          },
          {
            title: "Gas Line Repair",
            content: "Fixing damaged, corroded, or leaking lines. We use only approved materials and methods.",
            icon: "Wrench"
          },
          {
            title: "Appliance Connections",
            content: "Safe hookup of gas appliances with proper flex connectors, shut-offs, and sediment traps.",
            icon: "Plug"
          },
          {
            title: "Line Replacement",
            content: "Replacing old or undersized gas lines. Often recommended for galvanized steel lines over 30 years old.",
            icon: "RefreshCw"
          },
          {
            title: "Emergency Response",
            content: "24/7 emergency service for gas leaks and safety concerns. We arrive fast because gas emergencies can't wait.",
            icon: "AlertTriangle"
          }
        ]
      },
      {
        type: "warning",
        h2: "Know the Signs of a Gas Leak",
        urgent: true,
        content: "If you experience any of these, leave your home immediately and call 911, then call us:",
        items: [
          "Rotten egg or sulfur smell (mercaptan odor)",
          "Hissing or whistling sounds near gas lines",
          "Dead vegetation near gas line path",
          "Physical symptoms: dizziness, nausea, headaches"
        ],
        action: "After authorities clear the area, call us at (432) 552-8385 for repair."
      },
      {
        type: "process",
        h2: "Our Gas Line Installation Process",
        steps: [
          {
            number: "01",
            title: "Site Assessment",
            content: "We evaluate your gas meter capacity, determine the best route, and calculate proper pipe sizing."
          },
          {
            number: "02",
            title: "Permit & Planning",
            content: "We pull the required permits and create a detailed installation plan."
          },
          {
            number: "03",
            title: "Professional Installation",
            content: "Using black iron, CSST, or copper—whichever your application requires. All joints are properly sealed and supported."
          },
          {
            number: "04",
            title: "Pressure Testing",
            content: "Every installation is pressure tested to verify zero leaks before we turn on the gas."
          },
          {
            number: "05",
            title: "Inspection & Documentation",
            content: "We schedule the city inspection and provide documentation for your records."
          }
        ]
      },
      {
        type: "pricing",
        h2: "Gas Line Pricing in Odessa & Midland",
        priceItems: [
          { service: "Appliance Gas Hookup", range: "$150 - $500" },
          { service: "Gas Line Repair", range: "$200 - $800" },
          { service: "New Gas Line Run (per foot)", range: "$20 - $40" },
          { service: "Complete Line Replacement", range: "$1,500 - $5,000" },
          { service: "Outdoor Kitchen Gas Line", range: "$500 - $2,000" }
        ],
        note: "All gas work requires permits. Permit fees and inspection costs are included in our quotes."
      },
      {
        type: "local",
        h2: "Licensed Gas Plumbers Serving the Permian Basin",
        content: `Gas line work in Texas must be performed by licensed professionals. Our team holds all required certifications and stays current on code changes.

We've installed gas lines for homes, restaurants, and commercial buildings throughout Odessa, Midland, and surrounding communities. Whether you need a simple appliance connection or a complete commercial gas system, we have the expertise.`,
        areas: ["Odessa", "Midland", "Andrews", "Big Spring", "Crane", "Kermit", "Monahans", "Pecos"]
      }
    ],
    faqs: [
      {
        question: "Can I install a gas line myself in Texas?",
        answer: "No. Texas law requires gas work to be done by licensed professionals and inspected by local authorities. DIY gas work is illegal and dangerous."
      },
      {
        question: "How do I know if I have a gas leak?",
        answer: "The most common sign is a rotten egg smell. Other signs include hissing sounds near gas lines, dead plants near the line, and physical symptoms like dizziness. If you suspect a leak, leave immediately and call 911."
      },
      {
        question: "What type of pipe do you use for gas lines?",
        answer: "We use black iron pipe, CSST (corrugated stainless steel tubing), or copper depending on the application. All materials are approved for natural gas and propane."
      },
      {
        question: "How long does gas line installation take?",
        answer: "Simple appliance hookups take 1-2 hours. New line runs typically take 4-8 hours. We'll give you an accurate timeline during your estimate."
      },
      {
        question: "Do you work on propane systems?",
        answer: "Yes. We install and repair both natural gas and propane lines. The materials and techniques are similar, but propane has some additional requirements we're fully trained on."
      },
      {
        question: "How often should gas lines be inspected?",
        answer: "We recommend inspection every 3-5 years, or immediately if you notice any warning signs. Regular inspections catch small problems before they become dangerous."
      }
    ],
    internalLinks: [
      { text: "water heater installation", url: "/services/water-heater-installation-replacement-repair-midland-odessa-texas" },
      { text: "emergency plumbing", url: "/contact" },
      { text: "residential plumbing services", url: "/services/residential-plumbing" }
    ]
  },

  "drain-cleaning-midland-odessa-texas": {
    metaTitle: "Drain Cleaning Odessa & Midland TX | Hydro Jetting | Basin Plumbing",
    metaDescription: "Professional drain cleaning in Odessa & Midland. Hydro jetting, camera inspection, stubborn clogs cleared. 24/7 emergency. Call (432) 552-8385.",
    h1: "Drain Cleaning Services in Odessa & Midland",
    heroSubtitle: "Slow drains and stubborn clogs don't stand a chance against our professional equipment. We clear the blockage and show you exactly what caused it.",
    
    stats: [
      { value: "1000+", label: "Drains Cleared" },
      { value: "Same Day", label: "Service Available" },
      { value: "Video", label: "Inspection Included" },
      { value: "Guaranteed", label: "Results" }
    ],

    quickServices: [
      "Kitchen Drain Cleaning",
      "Bathroom Drain Clearing",
      "Main Line Cleaning",
      "Hydro Jetting",
      "Camera Inspection",
      "Preventive Maintenance"
    ],

    sections: [
      {
        type: "intro",
        h2: "Why Store-Bought Drain Cleaners Don't Work",
        content: `Here's what happens when you pour chemical drain cleaner down a slow drain: it sits on top of the clog, burns through some of the gunk, creates a small hole, and the rest hardens into an even worse blockage.

We see it all the time—homeowners who've been fighting the same drain for months with Drano, only to call us when it finally stops completely. By then, the chemical damage to their pipes makes the repair more expensive.

Professional drain cleaning actually removes the entire clog and cleans the pipe walls. That's why our results last—and chemical cleaners don't.`
      },
      {
        type: "local-challenges",
        h2: "Drain Problems Unique to Odessa & Midland",
        content: `West Texas water and soil conditions create specific drain challenges that homeowners in other parts of the country don't face. Understanding these local factors helps you prevent problems and know when to call for help.`,
        items: [
          {
            title: "Hard Water Mineral Buildup",
            content: "Odessa and Midland have some of the hardest water in Texas. Over time, calcium and magnesium deposits accumulate inside your drain pipes, narrowing them and catching debris that would otherwise flow through. We see pipes reduced to half their original diameter from mineral buildup alone."
          },
          {
            title: "Clay Soil Pipe Shifting",
            content: "The clay-heavy soil in the Permian Basin expands when wet and contracts when dry. This seasonal movement can shift pipes, creating low spots (bellies) where waste collects and clogs form. Camera inspection reveals these problem areas."
          },
          {
            title: "Tree Root Intrusion",
            content: "In a dry climate, tree roots aggressively seek moisture. They infiltrate sewer lines through tiny cracks and can completely block pipes within a few years. Mesquite trees are especially problematic in our area."
          },
          {
            title: "Older Home Plumbing",
            content: "Many Odessa and Midland homes were built during the oil booms of the 1950s-80s. Cast iron and clay sewer lines from that era are now 40-70 years old and prone to cracks, corrosion, and collapse."
          }
        ]
      },
      {
        type: "services-grid",
        h2: "Drain Cleaning Services We Offer",
        items: [
          {
            title: "Kitchen Drains",
            content: "Grease, food, and soap buildup cleared with professional equipment. We'll have your kitchen sink flowing freely.",
            icon: "ChefHat"
          },
          {
            title: "Bathroom Drains",
            content: "Hair, soap scum, and toothpaste clogs in sinks, showers, and tubs. We've seen it all—and cleared it all.",
            icon: "Bath"
          },
          {
            title: "Toilet Clogs",
            content: "From simple blockages to objects lodged in the trap, we clear toilets without damaging your fixture.",
            icon: "Droplet"
          },
          {
            title: "Main Sewer Line",
            content: "When multiple drains are slow, the main line is usually the culprit. We clear it and show you what's causing the problem.",
            icon: "GitBranch"
          },
          {
            title: "Hydro Jetting",
            content: "High-pressure water that scours pipe walls clean. The most thorough cleaning method available—especially effective for hard water buildup.",
            icon: "Waves"
          },
          {
            title: "Video Inspection",
            content: "We send a camera down your drain to see exactly what's happening. No guessing, no unnecessary work.",
            icon: "Video"
          }
        ]
      },
      {
        type: "comparison",
        h2: "Our Methods vs. DIY Drain Cleaning",
        options: [
          {
            title: "Chemical Drain Cleaners",
            best: "What most people try first",
            features: ["Creates partial opening at best", "Damages pipes over time", "Doesn't address root cause", "Temporary fix that returns"],
            negative: true
          },
          {
            title: "Professional Cleaning",
            best: "What actually works",
            features: ["Completely clears the blockage", "Cleans entire pipe walls", "Video confirms results", "Long-lasting solution"],
            link: { text: "Schedule drain cleaning", url: "/contact" }
          }
        ]
      },
      {
        type: "local-service-areas",
        h2: "Drain Cleaning Throughout the Permian Basin",
        content: "We provide fast drain cleaning service across Odessa, Midland, and surrounding communities. Our response times ensure you're never waiting long with a backed-up drain.",
        areas: [
          {
            city: "Odessa",
            response: "20-30 minutes",
            neighborhoods: ["Downtown Odessa", "UTPB Area", "Sherwood", "East Odessa", "West Odessa", "Music City Mall Area"]
          },
          {
            city: "Midland",
            response: "30-45 minutes",
            neighborhoods: ["Downtown Midland", "Grassland Estates", "Midkiff", "Greenwood", "Fannin Terrace", "Tall City"]
          },
          {
            city: "Surrounding Areas",
            response: "45-60 minutes",
            neighborhoods: ["Andrews", "Big Spring", "Crane", "Kermit", "Monahans", "Stanton"]
          }
        ]
      },
      {
        type: "signs",
        h2: "When to Call a Professional",
        items: [
          {
            title: "Multiple Slow Drains",
            content: "When more than one drain is slow, the problem is in your main line—not individual fixtures. This needs professional clearing.",
            link: { text: "Schedule main line cleaning", url: "/contact" }
          },
          {
            title: "Recurring Clogs",
            content: "If you're clearing the same drain repeatedly, there's a deeper issue. We'll find out what's really going on.",
            link: { text: "Request camera inspection", url: "/contact" }
          },
          {
            title: "Gurgling Sounds",
            content: "Bubbling or gurgling from drains indicates a venting problem or significant blockage that needs attention.",
            link: { text: "Learn about sewer services", url: "/services/sewer-line-replacement" }
          },
          {
            title: "Sewage Odors",
            content: "Sewer smells mean something is wrong—either a dry trap or a blockage causing backup. Don't ignore this.",
            link: { text: "Contact us immediately", url: "/contact" }
          },
          {
            title: "Water Backing Up",
            content: "Water coming up in one drain when you use another is a main line problem. This won't fix itself.",
            link: { text: "Emergency service available", url: "/contact" }
          }
        ]
      },
      {
        type: "pricing",
        h2: "Drain Cleaning Costs in Odessa & Midland",
        priceItems: [
          { service: "Single Drain Cleaning", range: "$100 - $250" },
          { service: "Toilet Clog Removal", range: "$100 - $300" },
          { service: "Main Line Cleaning", range: "$150 - $400" },
          { service: "Hydro Jetting", range: "$300 - $600" },
          { service: "Camera Inspection", range: "$100 - $300" }
        ],
        note: "Camera inspection is often credited toward the cleaning service if you proceed with us. Prices reflect 2025-2026 Permian Basin rates."
      },
      {
        type: "maintenance",
        h2: "Keep Your Drains Flowing",
        content: "A few simple habits prevent most drain clogs—especially important with our hard water:",
        tips: [
          { title: "Kitchen", items: ["Never pour grease down drains", "Use strainers to catch food", "Run hot water after each use", "Monthly baking soda flush for hard water buildup"] },
          { title: "Bathroom", items: ["Use hair catchers in showers", "Clean sink stoppers monthly", "Flush only toilet paper", "Install a water softener if possible"] },
          { title: "Prevention", items: ["Monthly enzyme treatment", "Annual professional cleaning", "Address slow drains early", "Camera inspection every 2-3 years for older homes"] }
        ],
        link: { text: "Ask about our maintenance plans", url: "/contact" }
      }
    ],
    faqs: [
      {
        question: "How often should I have drains professionally cleaned in Odessa or Midland?",
        answer: "For most Permian Basin homes, annual cleaning prevents problems. If you have older pipes (pre-1990), lots of trees near your sewer line, or heavy usage, every 6 months is better. Our hard water accelerates buildup, so we recommend cleaning more often than you might in other parts of Texas."
      },
      {
        question: "Is hydro jetting safe for old pipes?",
        answer: "Yes, when done by professionals who adjust pressure for your pipe type and condition. We always camera inspect first to make sure hydro jetting is appropriate. For very old cast iron or clay pipes common in older Odessa homes, we may recommend alternative methods."
      },
      {
        question: "Why does my drain keep clogging?",
        answer: "Recurring clogs usually mean a deeper problem: tree roots (common with mesquite), pipe damage from soil shifting, hard water mineral buildup, or buildup that wasn't fully cleared. A camera inspection will show us exactly what's happening."
      },
      {
        question: "Can drain cleaning damage my pipes?",
        answer: "Professional mechanical cleaning and hydro jetting won't damage pipes. Chemical cleaners, however, can corrode pipes—especially the older cast iron common in many Odessa and Midland homes from the 1950s-80s."
      },
      {
        question: "What's included in a camera inspection?",
        answer: "We insert a waterproof camera to see your entire drain line. You'll see the video with us and we'll explain exactly what we find. We can also provide a recording for your records or for insurance purposes."
      },
      {
        question: "Do you offer emergency drain cleaning in Midland?",
        answer: "Yes. Drain backups and overflows are emergencies. Call (432) 552-8385 any time—we're available 24/7 and can reach Midland within 30-45 minutes for emergencies."
      },
      {
        question: "Does hard water affect my drains?",
        answer: "Absolutely. Odessa and Midland have some of the hardest water in Texas. Mineral deposits accumulate inside drain pipes, narrowing them over time. Hydro jetting is especially effective at removing this scale buildup and restoring full pipe diameter."
      },
      {
        question: "What areas do you serve for drain cleaning?",
        answer: "We serve Odessa (20-30 min response), Midland (30-45 min), and surrounding communities including Andrews, Big Spring, Crane, Kermit, Monahans, Pecos, Stanton, and Seminole."
      }
    ],
    internalLinks: [
      { text: "sewer line services", url: "/services/sewer-line-replacement" },
      { text: "trenchless repair", url: "/services/trenchless-plumbing" },
      { text: "residential plumbing", url: "/services/residential-plumbing" },
      { text: "emergency plumber", url: "/contact" }
    ]
  },

  "water-heater-installation-replacement-repair-midland-odessa-texas": {
    metaTitle: "Water Heater Installation & Repair Odessa Midland TX | Basin Plumbing",
    metaDescription: "Expert water heater services in Odessa & Midland. Tank & tankless installation, same-day replacement available. Call (432) 552-8385.",
    h1: "Water Heater Services in Odessa & Midland",
    heroSubtitle: "No hot water is an emergency. We stock popular models for same-day replacement and offer repair options when that makes more sense.",
    
    stats: [
      { value: "800+", label: "Water Heaters Installed" },
      { value: "Same Day", label: "Replacement Available" },
      { value: "10 Year", label: "Warranty Options" },
      { value: "Financing", label: "Available" }
    ],

    quickServices: [
      "Tank Water Heater Installation",
      "Tankless Installation",
      "Water Heater Repair",
      "Emergency Replacement",
      "Maintenance Service",
      "Efficiency Upgrades"
    ],

    sections: [
      {
        type: "intro",
        h2: "When Your Water Heater Fails",
        content: `A family in East Midland called us on a Sunday morning—their water heater had died overnight, and they had out-of-town guests arriving that afternoon.

We had a new 50-gallon Rheem installed by 2 PM. Hot showers for everyone, crisis averted.

That's the kind of response you deserve when your water heater fails. Not "we can get to you Tuesday" but real help when you actually need it.`
      },
      {
        type: "services-grid",
        h2: "Water Heater Services",
        items: [
          {
            title: "Tank Water Heater Installation",
            content: "Traditional tank heaters in 40, 50, and 75-gallon sizes. Reliable, affordable, and we stock the popular models.",
            icon: "Database"
          },
          {
            title: "Tankless Installation",
            content: "Endless hot water, energy savings, and a 20+ year lifespan. We'll tell you if tankless makes sense for your home.",
            icon: "Zap"
          },
          {
            title: "Water Heater Repair",
            content: "Thermostat issues, element replacement, pilot light problems—we fix what's fixable.",
            icon: "Wrench"
          },
          {
            title: "Emergency Replacement",
            content: "No hot water can't wait. We stock common models and can often replace same-day.",
            icon: "AlertCircle"
          },
          {
            title: "Maintenance",
            content: "Annual flushing and inspection extends your water heater's life by years. Worth every penny.",
            icon: "Settings"
          },
          {
            title: "Efficiency Upgrades",
            content: "Older water heater working overtime? A new high-efficiency model can cut your energy costs significantly.",
            icon: "TrendingUp"
          }
        ]
      },
      {
        type: "comparison",
        h2: "Tank vs. Tankless: Honest Advice",
        content: "We install both and recommend based on what actually makes sense for you—not what makes us more money.",
        options: [
          {
            title: "Tank Water Heater",
            best: "Best for most Permian Basin homes",
            features: ["$1,200 - $2,500 installed", "Reliable and proven", "Works in power outages (gas)", "10-15 year lifespan", "Lower upfront cost"],
            link: { text: "Get a tank water heater quote", url: "/contact" }
          },
          {
            title: "Tankless Water Heater",
            best: "Best for high-demand households",
            features: ["$2,500 - $4,500 installed", "Endless hot water", "20-30% energy savings", "20+ year lifespan", "Requires water softener here"],
            link: { text: "Is tankless right for me?", url: "/contact" }
          }
        ]
      },
      {
        type: "signs",
        h2: "Signs Your Water Heater Needs Attention",
        items: [
          {
            title: "Water Isn't As Hot",
            content: "If your water isn't reaching the temperature it used to, the heating element or thermostat may be failing.",
            link: { text: "Schedule repair", url: "/contact" }
          },
          {
            title: "Running Out Faster",
            content: "Taking shorter showers because you run out of hot water? Sediment buildup or a failing element is likely the cause.",
            link: { text: "Request inspection", url: "/contact" }
          },
          {
            title: "Strange Noises",
            content: "Popping, rumbling, or crackling sounds mean sediment has built up and your heater is working too hard.",
            link: { text: "Book maintenance", url: "/contact" }
          },
          {
            title: "Rusty Water",
            content: "Rust-colored hot water (but clear cold water) means your tank is corroding inside. Replacement is usually needed.",
            link: { text: "Get replacement quote", url: "/contact" }
          },
          {
            title: "It's Over 10 Years Old",
            content: "Most tank water heaters last 10-15 years. If yours is getting up there, proactive replacement prevents emergency situations.",
            link: { text: "Explore upgrade options", url: "/contact" }
          },
          {
            title: "Visible Leaking",
            content: "Any leaking from the tank itself means replacement—the tank has corroded through and can't be repaired.",
            link: { text: "Emergency replacement", url: "/contact" }
          }
        ]
      },
      {
        type: "pricing",
        h2: "Water Heater Pricing",
        priceItems: [
          { service: "40-Gallon Gas Tank (installed)", range: "$1,200 - $2,000" },
          { service: "50-Gallon Gas Tank (installed)", range: "$1,400 - $2,300" },
          { service: "40-Gallon Electric Tank (installed)", range: "$1,000 - $1,800" },
          { service: "Tankless Gas (installed)", range: "$2,500 - $4,500" },
          { service: "Water Heater Repair", range: "$150 - $500" }
        ],
        note: "Prices include installation, old unit disposal, and permit. Financing available through GreenSky."
      },
      {
        type: "local",
        h2: "A Note About Hard Water",
        content: `The Permian Basin has notoriously hard water. This matters for water heaters because mineral buildup:

• Reduces efficiency (you pay more to heat water)
• Shortens lifespan (sediment damages the tank)
• Voids warranties on some tankless units

We recommend annual flushing for all water heaters here, and a water softener if you're going tankless. We'll give you straight advice on what your home actually needs.`,
        link: { text: "Ask about water treatment options", url: "/contact" }
      }
    ],
    faqs: [
      {
        question: "How long does water heater installation take?",
        answer: "Tank water heater replacement takes 2-4 hours. Tankless installation takes 4-8 hours, sometimes longer if we need to upgrade your gas line."
      },
      {
        question: "Should I repair or replace my water heater?",
        answer: "General rule: if it's under 8 years old and the repair is under $500, repair makes sense. Over 10 years old or major repairs? Replacement is usually smarter."
      },
      {
        question: "Is tankless worth the extra cost?",
        answer: "It depends on your situation. Large families who run out of hot water often benefit. Smaller households may not recoup the extra cost. We'll help you do the math."
      },
      {
        question: "What size water heater do I need?",
        answer: "General guidelines: 1-2 people need 30-40 gallons, 3-4 people need 40-50 gallons, 5+ people need 50-80 gallons. We'll assess your actual usage to make sure."
      },
      {
        question: "Do you install water heaters from big box stores?",
        answer: "We can, but there's usually no cost advantage. Our wholesale pricing is comparable, and units we supply include our warranty plus the manufacturer's warranty."
      },
      {
        question: "What brands do you recommend?",
        answer: "We install Rheem, A.O. Smith, and Bradford White for tanks; Rinnai and Navien for tankless. All are quality brands that perform well in our hard water."
      }
    ],
    internalLinks: [
      { text: "emergency plumbing", url: "/contact" },
      { text: "gas line services", url: "/services/gas-line-replacement-midland-odessa-texas" },
      { text: "residential plumbing", url: "/services/residential-plumbing" }
    ]
  },

  "sewer-line-replacement": {
    metaTitle: "Sewer Line Replacement Odessa & Midland TX | Basin Plumbing",
    metaDescription: "Professional sewer line services in Odessa & Midland. Camera inspection, trenchless options, 24/7 emergency. Call (432) 552-8385.",
    h1: "Sewer Line Services in Odessa & Midland",
    heroSubtitle: "Sewer problems are serious—but they don't always require digging up your entire yard. We'll show you exactly what's wrong and give you options.",
    
    stats: [
      { value: "400+", label: "Sewer Lines Serviced" },
      { value: "HD Camera", label: "Inspection" },
      { value: "Trenchless", label: "Options Available" },
      { value: "24/7", label: "Emergency Service" }
    ],

    quickServices: [
      "Camera Inspection",
      "Sewer Line Cleaning",
      "Root Removal",
      "Line Repair",
      "Trenchless Replacement",
      "Traditional Replacement"
    ],

    sections: [
      {
        type: "intro",
        h2: "The Truth About Sewer Line Problems",
        content: `Most homeowners ignore sewer line warning signs until raw sewage is backing up into their house. By then, what could have been a $500 cleaning is now a $5,000+ replacement.

We take a different approach: camera inspection first, always. We send a HD camera down your line and show you exactly what's happening. Sometimes it's just roots that need clearing. Sometimes there's a bigger problem. Either way, you see the evidence yourself before making any decisions.

No scare tactics. No unnecessary work. Just honest diagnosis and real solutions.`
      },
      {
        type: "services-grid",
        h2: "Sewer Line Services",
        items: [
          {
            title: "Video Camera Inspection",
            content: "See exactly what's in your sewer line. We record everything so you can see the problem (or confirm there isn't one).",
            icon: "Video"
          },
          {
            title: "Sewer Cleaning",
            content: "Professional cleaning removes buildup and clears blockages. Often all that's needed for recurring slow drains.",
            icon: "Droplets"
          },
          {
            title: "Root Removal",
            content: "Tree roots are the #1 cause of sewer problems in Odessa and Midland. We cut them out and can treat to prevent regrowth.",
            icon: "Trees"
          },
          {
            title: "Spot Repairs",
            content: "If damage is localized, we can often repair just that section instead of replacing the whole line.",
            icon: "Wrench"
          },
          {
            title: "Trenchless Replacement",
            content: "Replace your entire sewer line with minimal digging. Preserves landscaping and driveways.",
            icon: "ArrowRight"
          },
          {
            title: "Traditional Replacement",
            content: "Sometimes excavation is the best option—especially for collapsed lines or when upsizing is needed.",
            icon: "Construction"
          }
        ]
      },
      {
        type: "signs",
        h2: "Warning Signs of Sewer Line Problems",
        items: [
          {
            title: "Multiple Drains Are Slow",
            content: "When it's just one drain, it's a local clog. When multiple fixtures drain slowly, the main line is the problem.",
            link: { text: "Schedule camera inspection", url: "/contact" }
          },
          {
            title: "Sewage Odors",
            content: "You should never smell sewage in or around your home. If you do, something is wrong with your sewer line or venting.",
            link: { text: "Request inspection", url: "/contact" }
          },
          {
            title: "Gurgling Toilets",
            content: "Gurgling when you flush or when other fixtures drain indicates a venting issue or main line blockage.",
            link: { text: "Diagnose the problem", url: "/contact" }
          },
          {
            title: "Extra Green Grass",
            content: "A patch of grass that's greener and grows faster than the rest? Sewage is fertilizing it from below. That's a leak.",
            link: { text: "Check for leaks", url: "/contact" }
          },
          {
            title: "Foundation Issues",
            content: "Sewer leaks can erode soil under your foundation. If you're seeing new cracks, sewer line damage could be the cause.",
            link: { text: "Schedule assessment", url: "/contact" }
          }
        ]
      },
      {
        type: "comparison",
        h2: "Repair vs. Replace: How We Help You Decide",
        content: "Our camera inspection tells us exactly what we're dealing with:",
        options: [
          {
            title: "Good Candidates for Repair",
            features: ["Localized damage in one spot", "Roots that haven't damaged the pipe", "Pipe is newer PVC in good condition", "Problem is near the cleanout"],
            best: "Often $500 - $2,000"
          },
          {
            title: "Replacement Usually Needed",
            features: ["Multiple problem areas", "Pipe is over 50 years old", "Orangeburg, clay, or cast iron pipe", "Collapsed or severely damaged sections"],
            best: "Typically $3,000 - $10,000"
          }
        ],
        link: { text: "Learn about trenchless options", url: "/services/trenchless-plumbing" }
      },
      {
        type: "pricing",
        h2: "Sewer Line Service Costs",
        priceItems: [
          { service: "Camera Inspection", range: "$100 - $300" },
          { service: "Sewer Cleaning (Mechanical)", range: "$200 - $500" },
          { service: "Root Removal", range: "$300 - $600" },
          { service: "Spot Repair", range: "$500 - $2,000" },
          { service: "Full Replacement (Traditional)", range: "$3,000 - $8,000" },
          { service: "Full Replacement (Trenchless)", range: "$4,000 - $10,000" }
        ],
        note: "Camera inspection fee is credited toward repair if you proceed with us. Financing available."
      }
    ],
    faqs: [
      {
        question: "How do I know if my sewer line is the problem?",
        answer: "The telltale sign is multiple drains being slow or backing up at the same time. A camera inspection will confirm and show you exactly what's happening."
      },
      {
        question: "Can tree roots really damage sewer lines?",
        answer: "Absolutely. Roots seek water, and sewer lines are like a buffet. They enter through tiny cracks and grow until they block or break the pipe. It's the most common sewer problem we see."
      },
      {
        question: "What's the difference between trenchless and traditional replacement?",
        answer: "Traditional requires digging a trench along the entire line. Trenchless only needs small access points at each end. Trenchless preserves your yard but costs a bit more."
      },
      {
        question: "How long do sewer lines last?",
        answer: "PVC lasts 50-100 years. Older materials like clay, cast iron, and Orangeburg often fail after 40-50 years. If your home was built before 1980, the original sewer line is likely due for attention."
      },
      {
        question: "Will insurance cover sewer line replacement?",
        answer: "Standard homeowners insurance typically doesn't cover sewer lines. Some policies offer add-on coverage. We can provide documentation if you do have coverage."
      },
      {
        question: "How long does sewer line replacement take?",
        answer: "Traditional replacement takes 2-3 days. Trenchless can often be completed in 1-2 days. We'll give you a timeline based on your specific situation."
      }
    ],
    internalLinks: [
      { text: "trenchless plumbing", url: "/services/trenchless-plumbing" },
      { text: "drain cleaning", url: "/services/drain-cleaning-midland-odessa-texas" },
      { text: "emergency services", url: "/contact" }
    ]
  },

  "trenchless-plumbing": {
    metaTitle: "Trenchless Plumbing Odessa & Midland TX | No-Dig Pipe Repair",
    metaDescription: "Trenchless pipe repair in Odessa & Midland. Fix pipes without destroying your yard. Pipe lining, pipe bursting. Call (432) 552-8385.",
    h1: "Trenchless Plumbing in Odessa & Midland",
    heroSubtitle: "Modern pipe repair doesn't require destroying your yard. We fix underground pipes with minimal excavation—often in a single day.",
    
    stats: [
      { value: "200+", label: "Trenchless Jobs" },
      { value: "1 Day", label: "Most Jobs Done" },
      { value: "50+ Years", label: "New Pipe Life" },
      { value: "90% Less", label: "Digging" }
    ],

    quickServices: [
      "Pipe Lining (CIPP)",
      "Pipe Bursting",
      "Spot Repairs",
      "Sewer Line Replacement",
      "Water Line Replacement",
      "Camera Inspection"
    ],

    sections: [
      {
        type: "intro",
        h2: "Your Yard Doesn't Have to Be a Casualty",
        content: `A homeowner in Midland had a failing sewer line that ran directly under her pristine landscaping—mature trees, flagstone patio, the works. A traditional replacement would have meant tearing it all up.

Using pipe bursting, we replaced her entire 80-foot sewer line through two small access points. Total landscape damage: two 4x4 patches of grass. Her new line will outlast the original.

That's the power of trenchless technology. Same result, fraction of the disruption.`
      },
      {
        type: "services-grid",
        h2: "Trenchless Methods We Use",
        items: [
          {
            title: "Pipe Lining (CIPP)",
            content: "We insert a resin-coated liner into your existing pipe and cure it in place. Creates a new pipe inside the old one—no digging required.",
            icon: "Layers",
            best: "Best for: Cracked pipes, root damage, joint leaks"
          },
          {
            title: "Pipe Bursting",
            content: "A bursting head breaks apart the old pipe while pulling a new HDPE pipe into place. Complete replacement with minimal excavation.",
            icon: "Maximize",
            best: "Best for: Collapsed pipes, severe damage, upsizing"
          },
          {
            title: "Spot Repair",
            content: "When damage is localized to one area, we can repair just that section without touching the rest of the line.",
            icon: "Target",
            best: "Best for: Single break points, localized root damage"
          }
        ]
      },
      {
        type: "comparison",
        h2: "Trenchless vs. Traditional: Real Comparison",
        options: [
          {
            title: "Traditional Dig & Replace",
            features: ["Excavate entire pipe path", "2-5 days to complete", "Landscape restoration needed", "Sidewalks/driveways may be damaged", "Lower material cost, higher labor/restoration"],
            best: "Total cost: Similar or higher when including restoration"
          },
          {
            title: "Trenchless Methods",
            features: ["Only small access points needed", "Often completed in 1 day", "Minimal landscape damage", "Preserves hardscaping", "Higher material cost, lower labor/restoration"],
            best: "Total cost: Often comparable, always less disruption"
          }
        ]
      },
      {
        type: "process",
        h2: "How Trenchless Replacement Works",
        steps: [
          {
            number: "01",
            title: "Camera Inspection",
            content: "We scope your entire pipe to assess condition and confirm trenchless is appropriate."
          },
          {
            number: "02",
            title: "Pipe Cleaning",
            content: "We clean the pipe with hydro jetting to remove roots, scale, and debris."
          },
          {
            number: "03",
            title: "Access Points",
            content: "We dig small pits at each end of the pipe—typically 4x4 feet."
          },
          {
            number: "04",
            title: "Installation",
            content: "We pull the liner or new pipe through using specialized equipment."
          },
          {
            number: "05",
            title: "Curing/Connection",
            content: "Liners are cured in place. Burst pipes are connected to existing plumbing."
          },
          {
            number: "06",
            title: "Final Inspection",
            content: "Camera inspection confirms proper installation. Access points are backfilled."
          }
        ]
      },
      {
        type: "candor",
        h2: "When Trenchless Isn't the Answer",
        content: `We're trenchless advocates, but we're honest advocates. Trenchless isn't always the best choice:

**Traditional methods may be better when:**
• The pipe is completely collapsed (no path for liner or bursting head)
• Severe bellies (sagging sections) need correction  
• You need to significantly upsize the pipe
• Access points aren't feasible
• The pipe is too short (not cost-effective)

We'll show you the camera footage and explain your options honestly. Sometimes digging is the right call.`
      },
      {
        type: "pricing",
        h2: "Trenchless Pricing",
        priceItems: [
          { service: "Pipe Lining (per foot)", range: "$80 - $250" },
          { service: "Pipe Bursting (per foot)", range: "$100 - $300" },
          { service: "Average Residential Sewer", range: "$4,000 - $10,000" },
          { service: "Average Residential Water Line", range: "$3,000 - $6,000" },
          { service: "Spot Repair (Trenchless)", range: "$1,500 - $3,000" }
        ],
        note: "Final cost depends on pipe length, depth, and condition. We provide exact quotes after camera inspection."
      }
    ],
    faqs: [
      {
        question: "Is trenchless more expensive than traditional replacement?",
        answer: "Material costs are higher, but labor and restoration costs are much lower. When you factor in landscape repair, driveway restoration, and cleanup, trenchless is often the same price or cheaper."
      },
      {
        question: "How long do trenchless repairs last?",
        answer: "Pipe lining is rated for 50+ years. HDPE pipe from bursting is expected to last 100+ years. Both methods often outlast the original pipes."
      },
      {
        question: "Can trenchless fix a completely collapsed pipe?",
        answer: "Pipe lining requires the original pipe to maintain its basic shape. For collapsed pipes, pipe bursting may work since it replaces rather than lines. Some severe collapses require traditional excavation."
      },
      {
        question: "Will trenchless work under my driveway?",
        answer: "Yes! This is one of the biggest advantages. We can replace pipes under driveways, patios, and even building foundations without disturbing the surface."
      },
      {
        question: "How do I know if trenchless will work for my situation?",
        answer: "Camera inspection tells us. We'll scope your pipe and show you the footage. If trenchless is viable, we'll explain how. If not, we'll explain why and discuss alternatives."
      },
      {
        question: "Do you offer trenchless for both water and sewer lines?",
        answer: "Yes. Trenchless methods work for sewer lines, water lines, and other underground pipes. The specific technique varies based on pipe type and purpose."
      }
    ],
    internalLinks: [
      { text: "sewer line services", url: "/services/sewer-line-replacement" },
      { text: "water line replacement", url: "/services/waterline-replacement" },
      { text: "drain cleaning", url: "/services/drain-cleaning-midland-odessa-texas" }
    ]
  },

  "residential-plumbing": {
    metaTitle: "Residential Plumber Odessa & Midland TX | Basin Plumbing",
    metaDescription: "Your local residential plumber in Odessa & Midland. Repairs, installations, emergencies. Licensed, insured, available 24/7. Call (432) 552-8385.",
    h1: "Residential Plumbing Services in Odessa & Midland",
    heroSubtitle: "From dripping faucets to complete repiping, we handle everything plumbing in your home. Licensed, insured, and available when you need us.",
    
    stats: [
      { value: "5000+", label: "Homes Served" },
      { value: "Same Day", label: "Service Available" },
      { value: "Upfront", label: "Pricing Always" },
      { value: "24/7", label: "Emergency Line" }
    ],

    quickServices: [
      "Faucet Repair & Installation",
      "Toilet Repair & Replacement",
      "Garbage Disposal Service",
      "Leak Repair",
      "Pipe Repair & Repiping",
      "Fixture Installation"
    ],

    sections: [
      {
        type: "intro",
        h2: "Your Home's Plumbing Partner",
        content: `We're the plumbers Odessa and Midland families call when something goes wrong—and when they want to upgrade something that's working fine but could work better.

Need a quick fix? We're responsive. Planning a bathroom remodel? We're thorough. Middle-of-the-night emergency? We answer the phone.

This is what we do. Every day. And we're pretty good at it.`
      },
      {
        type: "services-grid",
        h2: "Residential Services",
        items: [
          {
            title: "Faucet Services",
            content: "Repair dripping faucets or upgrade to something new. We work with all brands and styles.",
            icon: "Droplet",
            link: { text: "Fix my faucet", url: "/contact" }
          },
          {
            title: "Toilet Services",
            content: "Running toilets, weak flush, replacements—we've fixed thousands of toilets in Permian Basin homes.",
            icon: "Circle",
            link: { text: "Toilet help", url: "/contact" }
          },
          {
            title: "Garbage Disposals",
            content: "Installation, repair, and unjamming. We'll get your disposal working or install a better one.",
            icon: "Trash2",
            link: { text: "Disposal service", url: "/contact" }
          },
          {
            title: "Water Heaters",
            content: "Repair, replacement, and new installation for tank and tankless water heaters.",
            icon: "Thermometer",
            link: { text: "Water heater help", url: "/services/water-heater-installation-replacement-repair-midland-odessa-texas" }
          },
          {
            title: "Leak Repair",
            content: "Drips, sprays, seeps—we find leaks and fix them. Emergency service for major leaks.",
            icon: "AlertTriangle",
            link: { text: "Stop my leak", url: "/contact" }
          },
          {
            title: "Drain Cleaning",
            content: "Slow drains and clogs cleared with professional equipment. Camera inspection available.",
            icon: "ArrowDown",
            link: { text: "Clear my drain", url: "/services/drain-cleaning-midland-odessa-texas" }
          },
          {
            title: "Pipe Repair & Repiping",
            content: "Fix leaking pipes or replace failing pipe systems. PEX and copper options.",
            icon: "GitBranch",
            link: { text: "Pipe services", url: "/contact" }
          },
          {
            title: "Fixture Installation",
            content: "New sinks, tubs, showers, and fixtures installed correctly the first time.",
            icon: "Plus",
            link: { text: "Schedule installation", url: "/contact" }
          }
        ]
      },
      {
        type: "why-us",
        h2: "Why Permian Basin Families Choose Us",
        items: [
          {
            title: "We Answer the Phone",
            content: "Call us and talk to a real person. Nights, weekends, holidays—someone is always here."
          },
          {
            title: "Upfront Pricing",
            content: "You know what it costs before we start. No surprises on the invoice."
          },
          {
            title: "We Clean Up",
            content: "Shoe covers, drop cloths, and we take our trash with us. Your home stays your home."
          },
          {
            title: "We Explain Things",
            content: "We'll show you what's wrong and explain your options. You make the call."
          },
          {
            title: "We Stand Behind Our Work",
            content: "If something isn't right, we come back and make it right. Period."
          }
        ]
      },
      {
        type: "pricing",
        h2: "Common Repair Costs",
        priceItems: [
          { service: "Faucet Repair", range: "$75 - $200" },
          { service: "Toilet Repair", range: "$75 - $250" },
          { service: "Garbage Disposal Install", range: "$200 - $450" },
          { service: "Leak Repair", range: "$150 - $400" },
          { service: "Drain Cleaning", range: "$100 - $300" }
        ],
        note: "These are typical ranges. We provide exact pricing before starting work."
      },
      {
        type: "emergency",
        h2: "24/7 Emergency Service",
        content: `Some plumbing problems can't wait until Monday:

• Burst pipes flooding your home
• Sewer backing up into the house
• Gas leaks (call 911 first, then us)
• No water at all
• Water heater failure

Call (432) 552-8385 any time. We're here.`,
        buttons: [
          { text: "Call Now: (432) 552-8385", url: "tel:4325528385", primary: true },
          { text: "Schedule Online", url: "/contact", primary: false }
        ]
      }
    ],
    faqs: [
      {
        question: "Do you charge for estimates?",
        answer: "Estimates for most work are free. If diagnostic work is needed (like leak detection or camera inspection), there may be a fee—which we credit toward the repair if you hire us."
      },
      {
        question: "How quickly can you get here?",
        answer: "For emergencies, usually 30-60 minutes. For routine service, we often have same-day or next-day availability."
      },
      {
        question: "Do you work on weekends?",
        answer: "Yes. We offer Saturday appointments and 24/7 emergency service including Sundays and holidays."
      },
      {
        question: "Are your plumbers licensed?",
        answer: "Every plumber on our team is licensed by the State of Texas. We're also fully bonded and insured."
      },
      {
        question: "Do you offer financing?",
        answer: "Yes. For larger projects like water heater replacement or repiping, we offer financing through GreenSky with affordable monthly payments."
      },
      {
        question: "What areas do you serve?",
        answer: "We serve Odessa, Midland, and surrounding Permian Basin communities including Andrews, Big Spring, Crane, Kermit, Monahans, Pecos, Stanton, and Seminole."
      }
    ],
    internalLinks: [
      { text: "water heater services", url: "/services/water-heater-installation-replacement-repair-midland-odessa-texas" },
      { text: "drain cleaning", url: "/services/drain-cleaning-midland-odessa-texas" },
      { text: "gas line services", url: "/services/gas-line-replacement-midland-odessa-texas" },
      { text: "Midland plumbing", url: "/plumbers-in-midland" }
    ]
  },

  "medical-gas-installation": {
    metaTitle: "Medical Gas Installation Odessa & Midland TX | Basin Plumbing",
    metaDescription: "Certified medical gas installation in Odessa & Midland. ASSE 6010 certified. Hospitals, clinics, dental offices. Call (432) 552-8385.",
    h1: "Medical Gas Installation in Odessa & Midland",
    heroSubtitle: "ASSE 6010 certified installers for healthcare facilities. Oxygen, medical air, vacuum systems—installed and tested to NFPA 99 standards.",
    
    stats: [
      { value: "ASSE 6010", label: "Certified" },
      { value: "NFPA 99", label: "Compliant" },
      { value: "50+", label: "Facilities Served" },
      { value: "Zero", label: "Safety Incidents" }
    ],

    quickServices: [
      "Oxygen Systems",
      "Medical Air Systems",
      "Vacuum Systems",
      "Nitrous Oxide Systems",
      "WAGD Systems",
      "Testing & Certification"
    ],

    sections: [
      {
        type: "intro",
        h2: "Healthcare-Grade Plumbing Services",
        content: `Medical gas systems are life-safety equipment. They're not regular plumbing—they require specialized training, specific certifications, and strict adherence to healthcare facility codes.

Basin Plumbing's ASSE 6010 certified installers work with hospitals, surgery centers, dental practices, and veterinary clinics throughout Odessa, Midland, and the Permian Basin.

We understand the stakes. Every installation is done right, tested thoroughly, and documented completely.`
      },
      {
        type: "services-grid",
        h2: "Medical Gas Services",
        items: [
          {
            title: "Oxygen Systems",
            content: "Bulk systems, cylinder manifolds, and distribution piping for patient care areas.",
            icon: "Wind"
          },
          {
            title: "Medical Air",
            content: "Compressor systems and distribution for respiratory therapy and surgical applications.",
            icon: "Fan"
          },
          {
            title: "Vacuum Systems",
            content: "Medical suction for surgical and patient care applications.",
            icon: "ArrowDownCircle"
          },
          {
            title: "Nitrous Oxide",
            content: "Manifold systems and piping for sedation in dental and surgical settings.",
            icon: "Smile"
          },
          {
            title: "WAGD Systems",
            content: "Waste anesthetic gas disposal to protect staff from exposure.",
            icon: "ArrowUpRight"
          },
          {
            title: "Testing & Certification",
            content: "Required testing for new installations and modifications per NFPA 99.",
            icon: "CheckCircle"
          }
        ]
      },
      {
        type: "facilities",
        h2: "Facilities We Serve",
        categories: [
          {
            title: "Hospitals & Medical Centers",
            items: ["Operating rooms", "ICU and patient rooms", "Emergency departments", "Labor and delivery"]
          },
          {
            title: "Outpatient Facilities",
            items: ["Ambulatory surgery centers", "Dialysis centers", "Imaging centers", "Urgent care clinics"]
          },
          {
            title: "Dental & Specialty",
            items: ["Dental offices", "Oral surgery centers", "Veterinary hospitals", "Research facilities"]
          }
        ]
      },
      {
        type: "compliance",
        h2: "Compliance & Standards",
        content: `All our medical gas work meets:

**NFPA 99** - Health Care Facilities Code
**ASSE 6010** - Medical Gas Installer Certification  
**CGA Standards** - Compressed Gas Association requirements
**State & Local Codes** - Texas regulations and local requirements

We provide complete documentation for Joint Commission, state inspections, and your facility records.`
      }
    ],
    faqs: [
      {
        question: "What certifications do your medical gas installers have?",
        answer: "Our installers are ASSE 6010 certified—the national standard for medical gas system installation. We maintain current certifications and all required state licenses."
      },
      {
        question: "Can you add outlets to an existing system?",
        answer: "Yes. We can extend existing systems and add new outlets. All modifications require verification testing per NFPA 99 before use."
      },
      {
        question: "Do you work on dental office gas systems?",
        answer: "Absolutely. We install nitrous oxide, compressed air, and vacuum systems for dental practices. These systems must meet the same NFPA 99 standards as hospital systems."
      },
      {
        question: "What testing is required for new installations?",
        answer: "NFPA 99 requires pressure testing, purity testing, cross-connection testing, and alarm verification. We perform all required tests and provide documentation."
      },
      {
        question: "Do you offer emergency medical gas service?",
        answer: "Yes. We understand healthcare facilities operate 24/7. Call (432) 552-8385 for emergency response."
      },
      {
        question: "How often do medical gas systems need inspection?",
        answer: "NFPA 99 requires annual alarm testing, quarterly source equipment inspection, and periodic outlet maintenance. We offer maintenance contracts to keep your facility compliant."
      }
    ],
    internalLinks: [
      { text: "commercial plumbing", url: "/services" },
      { text: "gas line services", url: "/services/gas-line-replacement-midland-odessa-texas" }
    ]
  }
};

export default serviceContent;
